<script>
  import { fade, fly, slide } from "svelte/transition";
  import { linear } from "eases";
  import StaticArtwork from "./StaticArtwork.svelte";
  import downloadIcon from "../icons/download2.svelte";
  import { hexToEncoding, encodingToHex } from "bitframes";
  import * as local from "../util/local.js";
  import * as Hotkeys from "../util/hotkeys.js";
  import Button from "./Button.svelte";
  import undoIcon from "../icons/undothin.svelte";
  import MintButton from "./MintButton.svelte";
  import diceIcon from "../icons/dice.svelte";
  import {
    createEventDispatcher,
    getContext,
    onMount,
    setContext,
  } from "svelte";
  import { getTimestamp } from "../util/save";
  import EditPanel from "./EditPanel.svelte";
  import { copy as clipboardCopy } from "../util/clipboard.js";
  import CollapsePanelButton from "./CollapsePanelIcon.svelte";
  import ToolTip from "./ToolTip.svelte";
  import {
    CONTRACTS,
    isAfterLaunchStore,
    LINK_EDITIONS,
    LINK_GALLERY,
  } from "../util/constants";
  import { fetchTokenDetails } from "../util/data-fetcher";
  import BreadcrumbSlash from "./BreadcrumbSlash.svelte";
  import LinkButton from "./LinkButton.svelte";

  export let edition = false;
  export let editionID = -1;
  let loadingEdition = false;

  const EDITION_CRUMB_HEIGHT = 40;
  let artPanelEditionCrumbHeight = EDITION_CRUMB_HEIGHT;
  $: {
    if (edition) {
      artPanelEditionCrumbHeight = EDITION_CRUMB_HEIGHT;
    } else {
      artPanelEditionCrumbHeight = 0;
    }
  }

  let _lastEditionID;
  let editionDefaultSeed;
  $: {
    if (_lastEditionID !== editionID) {
      _lastEditionID = editionID;
      if (editionID >= 0 && edition) grabEditionID();
    }
  }

  const MOBILE_BREAKPOINT = 720;
  const SHARE_POPUP_BREAKPOINT = 500;
  const NAV_BAR_SIZE = 46;
  const TOOL_BAR_SIZE = 65;
  const modal = getContext("modal");
  const toast = getContext("toast");
  let displayPadding = 20;
  let smallButtons = false;
  let showButtonText = false;

  let isDraggingPanel = false;

  let shareOpen = false;
  let sharePopupEl, shareButtonContainer;

  let innerScroll = true;

  let zoom = local.get("zoom", false);
  $: local.set("zoom", zoom);

  let sidePanelVisible = local.get("side-panel-visible", false);
  $: local.set("side-panel-visible", sidePanelVisible);

  const previewStore = getContext("encoding-preview");

  const undoRedo = getContext("undo-redo");
  const undoRedoState = undoRedo.state;

  let hasSeenExpandTip = local.get("edit-page-seen-tooltip-expand", false);
  let hasSeenDragTip = local.get("edit-page-seen-tooltip-drag", false);

  let curToolTipHint;
  $: hasSeenDragTip,
    hasSeenExpandTip,
    sidePanelVisible,
    curToolTipHint,
    updateHint();
  updateHint();

  function updateHint() {
    if (!hasSeenDragTip || !hasSeenExpandTip) {
      if (sidePanelVisible && !hasSeenDragTip) {
        if (curToolTipHint !== "drag") curToolTipHint = "drag";
      }
      if (!sidePanelVisible && !hasSeenExpandTip) {
        if (curToolTipHint !== "expand") curToolTipHint = "expand";
      }
    }
    if (hasSeenDragTip && hasSeenExpandTip && curToolTipHint) {
      if (curToolTipHint != null) curToolTipHint = null;
    }
  }

  let hd = true;
  const dispatch = createEventDispatcher();

  const encodingStore = getContext("encoding");

  let encoding;
  let isShowingEditionEncoding = false;
  const unsubEncodingStore = encodingStore.subscribe((e) => {
    encoding = e;
    isShowingEditionEncoding = encoding && encoding[0] == 0x01;
  });

  async function grabEditionID() {
    loadingEdition = true;
    editionDefaultSeed = null;
    let info = null;
    if (edition && editionID >= 0) {
      info = await fetchTokenDetails({
        token_id: editionID,
        contract: CONTRACTS.NFT_LIMITED.address,
      });
      if (!info || info.token_id == null) {
        info = null;
        toast.error("Could not find edition");
        console.error("No token by ID", editionID);
      } else {
        const newEncodingHex = info.seed;
        editionDefaultSeed = newEncodingHex;
        if (encodingToHex(encoding) !== newEncodingHex) {
          setNewEncoding(hexToEncoding(newEncodingHex));
        }
      }
    }
    loadingEdition = false;
  }

  let windowWidth, windowHeight;
  let artPanelWidth, artPanelHeight, editPanelWidth, editPanelHeight;
  let lastDragState = null;
  let lastDragTime = performance.now();
  let deltaDrag = 0;
  let dragSize = [0, 0];

  resize();
  $: sidePanelVisible, resize();

  let curPreview;
  const unsubPreviewStore = previewStore.subscribe((v) => {
    curPreview = v;
  });

  onMount(() => {
    return () => {
      unsubPreviewStore();
      unsubEncodingStore();
    };
  });

  const canvasEnv = { save: null };
  setContext("canvas", canvasEnv);

  function clearTooltip() {
    if (curToolTipHint == "expand") {
      local.set("edit-page-seen-tooltip-expand", true);
      hasSeenExpandTip = true;
      curToolTipHint = null;
    }
    if (curToolTipHint == "drag") {
      local.set("edit-page-seen-tooltip-drag", true);
      hasSeenDragTip = true;
      curToolTipHint = null;
    }
  }

  function setDragState(x, y) {
    if (!lastDragState) {
      lastDragState = [x, y];
    }
    const dx = x - lastDragState[0];
    const dy = y - lastDragState[1];
    lastDragState[0] = x;
    lastDragState[1] = y;

    dragSize[0] += dx;
    dragSize[1] += dy;
    deltaDrag += Math.hypot(dx, dy);
    if (deltaDrag > 0) clearTooltip();
    resize();
    // const u = Math.max(0, Math.min(1, lastDragState[0] / artPanelWidth));
    // const v = Math.max(
    //   0,
    //   Math.min(1, (lastDragState[1] - NAV_BAR_SIZE) / artPanelHeight)
    // );
    // console.log(v);
  }

  function fadeOut(node, { delay = 0, duration = 400, easing = linear } = {}) {
    return {
      delay,
      duration,
      easing,
      css: (t) => `opacity: ${t}`,
    };
  }

  function fadeIn(node, { delay = 0, duration = 400, easing = linear } = {}) {
    console.log("FADE!!");
    return {
      delay,
      duration,
      easing,
      css: (t) => `opacity: ${t}`,
    };
  }

  function fadePrep(node) {
    // node.style.opacity = "0";
    return {
      destroy() {},
    };
  }

  function computeNewSize(opts = {}) {
    const {
      min = 0.45,
      max = 0.55,
      minDrag = 0.25,
      maxDrag = 0.75,
      minSize = 0,
      maxSize = Infinity,
      contentSize,
      target,
      dimension = 0,
    } = opts;
    if (sidePanelVisible) {
      let cur = Math.round(
        Math.max(min * contentSize, Math.min(max * contentSize, target))
      );
      cur += dragSize[dimension];
      cur = Math.round(
        Math.max(minDrag * contentSize, Math.min(maxDrag * contentSize, cur))
      );
      cur = Math.max(minSize, Math.min(maxSize, cur));
      return cur;
    } else {
      return Math.round(contentSize);
    }
  }

  function resize() {
    windowWidth = window.innerWidth;
    windowHeight = window.innerHeight;

    if (windowWidth <= MOBILE_BREAKPOINT) {
      if (zoom) zoom = false;
    }

    const contentHeight = windowHeight - NAV_BAR_SIZE;
    const contentHeightMinusTools = contentHeight - TOOL_BAR_SIZE;

    const idealSize = Math.min(contentHeightMinusTools, windowWidth);
    let factor;

    if (windowWidth > MOBILE_BREAKPOINT) {
      artPanelWidth = computeNewSize({
        min: 0.45,
        max: 0.55,
        minSize: 360,
        maxSize: windowWidth - 300,
        contentSize: windowWidth,
        target: idealSize,
        dimension: 0,
      });
      artPanelHeight = contentHeightMinusTools;
      editPanelWidth = windowWidth - artPanelWidth;
      editPanelHeight = contentHeight;
      factor = artPanelWidth / windowWidth;
    } else {
      artPanelHeight = computeNewSize({
        min: 0.4,
        max: 0.475,
        // minSize: 64,
        // maxSize: contentHeightMinusTools - 10,
        contentSize: contentHeightMinusTools,
        target: idealSize,
        dimension: 1,
      });
      artPanelWidth = windowWidth;
      editPanelWidth = windowWidth;
      editPanelHeight = contentHeightMinusTools - artPanelHeight;
      factor = artPanelHeight / contentHeight;
    }

    if (artPanelWidth > MOBILE_BREAKPOINT) {
      if (shareOpen) shareOpen = false;
    }
    smallButtons = artPanelWidth < 500;
    // showButtonText = artPanelWidth > 700;

    // if (windowWidth > 1000 && factor >= 0.5) displayPadding = 60;
    // else if (windowWidth > 900 && factor >= 0.5) displayPadding = 40;
    // else displayPadding = 20;
  }

  function randomize(ev) {
    ev.preventDefault();
    dispatch("randomize", { withLocks: true, edition });
  }

  async function share() {
    const url = new URL(window.location.href);
    url.search = `?share&encoding=${encodingToHex(encoding)}`;
    try {
      shareOpen = false;
      const ret = await clipboardCopy(url.href);
      if (ret) {
        if (toast) toast.show("Share link copied to clipboard!");
      }
    } catch (error) {
      console.error(error.message);
      if (toast) toast.error(`Error copying to clipboard`);
    }
  }

  async function save(ev) {
    if (ev.detail && ev.detail.originalEvent) {
      ev.detail.originalEvent.preventDefault();
    }

    if (canvasEnv && canvasEnv.save) {
      const timestamp = getTimestamp();
      shareOpen = false;
      await canvasEnv.save({
        filename: [timestamp, encodingToHex(encoding)].join("-") + ".png",
      });
      toast.show("PNG file downloaded!");
    }
  }

  function setNewEncoding(enc) {
    dispatch("encodingChanged", enc);
  }

  function togglePanel() {
    sidePanelVisible = !sidePanelVisible;
  }

  function panelHotkey() {
    const unsub = Hotkeys.mount(".", togglePanel);
    return {
      destroy: () => {
        unsub();
      },
    };
  }

  function resetDrag() {
    lastDragState = null;
    lastDragTime = performance.now();
    deltaDrag = 0;
    isDraggingPanel = true;
  }

  function doPrint() {
    modal.show("print", { title: "Print" });
  }

  function doUpdateEdition() {
    modal.show("refresh", { title: "Update Encoding", editionID });
  }
</script>

<div class="container noselect" class:innerScroll use:panelHotkey>
  <div
    class="artwork-panel"
    class:sidePanelVisible
    style="width: {artPanelWidth}px; height: {artPanelHeight +
      TOOL_BAR_SIZE}px;"
  >
    {#if edition}
      <div class="edition-crumb" style="height: {artPanelEditionCrumbHeight}px">
        <LinkButton to={`/${LINK_EDITIONS}/token/${editionID}`}>..</LinkButton>
        <BreadcrumbSlash />
        32 Bit Edition #{editionID}
      </div>
    {/if}
    <div
      class="artwork"
      style="width: {artPanelWidth}px; height: {artPanelHeight -
        artPanelEditionCrumbHeight}px;"
    >
      <StaticArtwork
        {displayPadding}
        allowClickZoom={windowWidth > MOBILE_BREAKPOINT}
        allowTouchZoom={true}
        autoCanvasResolution
        bind:zoom
        hotkeys={true}
        hd={curPreview ? false : hd}
        margin={true}
        encoding={curPreview ? curPreview : encoding}
        on:save={save}
      />
      {#if shareOpen && artPanelWidth <= SHARE_POPUP_BREAKPOINT}
        <div class="share-popup" bind:this={sharePopupEl}>
          <Button fitHeight text="Save Image" on:click={save}></Button>
          <Button fitHeight text="Copy URL" on:click={share}></Button>
        </div>
      {/if}

      {#if curToolTipHint}
        {#key curToolTipHint}<div
            class="tool-tip-fader"
            use:fadePrep
            in:fadeIn={{ delay: 500 }}
            out:fadeOut
          >
            <ToolTip mode={curToolTipHint}></ToolTip>
          </div>{/key}
      {/if}
    </div>

    <div class="edit-tools noselect" style="height: {TOOL_BAR_SIZE}px;">
      <div class="rest">
        <div class="left">
          <Button
            fitHeight
            on:click={randomize}
            hotkey="r"
            small={smallButtons}
            icon={diceIcon}
            text="Random"
          />
          <Button
            fitHeight
            alt="Undo (Ctrl/Cmd + Z)"
            on:click={undoRedo.undo}
            icon={undoIcon}
            enabled={$undoRedoState.canUndo}
            small={smallButtons}
            square
          />
          <Button
            fitHeight
            alt="Redo (Ctrl/Cmd + Shift + Z)"
            on:click={undoRedo.redo}
            enabled={$undoRedoState.canRedo}
            small={smallButtons}
            icon={undoIcon}
            square
            flip
          />
        </div>
        <div class="right">
          {#if windowWidth > MOBILE_BREAKPOINT}
            <!--  -->
          {/if}
          <!-- <div class="share-button-container" bind:this={shareButtonContainer}>
            {#if artPanelWidth > SHARE_POPUP_BREAKPOINT}
              <Button
                alt="Download PNG (Ctrl/Cmd + S)"
                fitHeight
                icon={downloadIcon}
                text={showButtonText ? "Download" : ""}
                small={smallButtons}
                on:click={save}
              ></Button>
              <Button
                alt="Copy Share URL"
                fitHeight
                small={smallButtons}
                icon={shareIcon}
                text={showButtonText ? "Share" : ""}
                on:click={share}
              ></Button>
            {:else if artPanelWidth > 300}
              <Button
                fitHeight
                small={smallButtons}
                icon={shareIcon}
                radio
                bind:toggled={shareOpen}
              ></Button>
            {/if}
          </div> -->
          {#if $isAfterLaunchStore}
            {#if isShowingEditionEncoding && edition}
              {#if editionDefaultSeed == encodingToHex(encoding)}
                <div class="same-seed-error"></div>
              {:else}
                <Button
                  small={smallButtons}
                  fitHeight
                  publish
                  enabled={editionDefaultSeed !== encodingToHex(encoding)}
                  text="Update"
                  on:click={doUpdateEdition}
                />
              {/if}
            {/if}
          {:else}<MintButton small={smallButtons} fitHeight />{/if}
        </div>
      </div>
      <button
        class="collapse-panel-button noselect"
        class:active={sidePanelVisible}
        on:mousedown|nonpassive={(ev) => {
          if (ev.which === 1) lastDragTime = performance.now();

          if (ev.which === 1 && sidePanelVisible) {
            ev.preventDefault();

            resetDrag();
          }
          // clearTooltip();
        }}
        on:mouseup|nonpassive={(ev) => {
          if (ev.which === 1 && isDraggingPanel) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
            ev.stopPropagation();
            isDraggingPanel = false;
          }
        }}
        on:touchstart|passive={(ev) => {
          lastDragTime = performance.now();

          if (sidePanelVisible && !isDraggingPanel) {
            // ev.preventDefault();
            resetDrag();
          }
        }}
        on:click|nonpassive={(ev) => {
          if (deltaDrag > 0) {
            const timeDelta = performance.now() - lastDragTime;
            // slow click is ignored
            if (timeDelta > 300) return;
          }
          if (deltaDrag > 5) {
            return;
          }
          ev.preventDefault();
          clearTooltip();
          togglePanel();
        }}
      >
        <!-- <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 10.5H2M2 4H19M2 17H19"
              stroke="black"
              stroke-width="1.5"
              stroke-linejoin="bevel"
            />
          </svg> -->
        <CollapsePanelButton rotate={windowWidth <= MOBILE_BREAKPOINT} />
      </button>
    </div>
  </div>
  <div
    class="edit-panel"
    style={`
    display: ${sidePanelVisible ? "initial" : "none"};
    width: ${editPanelWidth}px; height: ${editPanelHeight}px;`}
  >
    <div class="edit-panel-container">
      <!-- <div style="width: 200px;height: 700px; background: pink;"></div> -->
      <EditPanel
        {edition}
        innerScroll
        isMobile={windowWidth <= MOBILE_BREAKPOINT}
        on:encodingChanged
        on:share={share}
        on:save={save}
      ></EditPanel>
    </div>
  </div>
</div>

<svelte:window
  on:mouseup|passive={(ev) => {
    if (ev.which == 1) isDraggingPanel = false;
  }}
  on:touchend|passive={(ev) => {
    isDraggingPanel = false;
  }}
  on:mousemove|passive={(ev) => {
    if (isDraggingPanel) {
      const x = ev.clientX;
      const y = ev.clientY;
      setDragState(x, y);
    }
  }}
  on:touchmove|passive={(ev) => {
    if (isDraggingPanel && ev.touches.length) {
      const x = ev.touches[0].clientX;
      const y = ev.touches[0].clientY;
      setDragState(x, y);
    }
  }}
  on:resize|passive={resize}
  on:touchstart|passive={(ev) => {
    // const isHitSharePopup =
    //   ev.target === sharePopupEl ||
    //   (sharePopupEl && sharePopupEl.contains
    //     ? sharePopupEl.contains(ev.target)
    //     : false);
    // const isHitShareButton =
    //   ev.target === shareButtonContainer ||
    //   (shareButtonContainer && shareButtonContainer.contains
    //     ? shareButtonContainer.contains(ev.target)
    //     : false);
    // if (isHitShareButton || isHitSharePopup) {
    //   // ignore
    // } else {
    //   shareOpen = false;
    // }
  }}
/>

<style>
  .container {
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    align-items: flex-start;
    /* height: 100%; */
    flex-grow: 1;
    flex-shrink: 1;
    position: relative;
    /* height: 200px; */
    /* height: calc(100vh - 46px); */
  }
  .innerScroll {
    overflow: hidden;
  }
  .edition-crumb {
    display: flex;
    align-items: center;
    width: 100%;
    padding-left: 20px;
    box-sizing: border-box;
    gap: 10px;
    border-bottom: 1px dashed black;
  }
  .artwork-panel,
  .edit-panel {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    /* height: 100%; */
    flex-grow: 1;
    flex-shrink: 1;
  }
  .artwork-panel {
    overflow: hidden;
    /* aspect-ratio: 1; */
    /* overflow: hidden; */
    border-right: 1px solid black;
    /* background: pink; */
    /* flex-basis: 55%; */
    flex-shrink: 0;
    flex-grow: 0;
    justify-content: flex-start;
  }
  .edit-panel {
    justify-content: flex-start;
    align-items: flex-start;
    /* padding: 20px; */
    box-sizing: border-box;
    /* flex-basis: 40%; */
  }
  .innerScroll .edit-panel {
    overflow: hidden;
  }
  .artwork {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    width: 100%;
    height: 100%;
  }
  .tool-tip-fader {
    position: absolute;
    bottom: 20px;
    right: 10px;
  }
  .same-seed-error {
  }
  .share-popup {
    /* border-right: 1px solid black; */

    flex-grow: 1;
    flex-shrink: 1;
    /* max-width: 50%; */
    /* align-self: center; */
    /* justify-self: center; */
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background: white;
    /* padding: 20px; */
    border-top: 1px solid black;
    border-left: 1px solid black;
    margin-right: 0px;
    gap: 10px;
    padding: 10px;
    /* width: 200px; */
  }
  :global(.overlay-buttons *) {
    pointer-events: initial;
  }

  .collapse-panel-button {
    /* position: absolute; */
    /* right: 0; */
    /* top: 0; */
    /* margin-bottom: 10px; */
    background: white;
    border-radius: 0;
    border: 0;
    padding: 0;
    flex-grow: 0;
    flex-shrink: 0;

    /* box-shadow: 0px 4px 0px black; */
    border-left: 1px solid black;
    /* border-left: 1px solid black; */
    width: 50px;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .collapse-panel-button.active {
    background: var(--accent-color);
  }
  .edit-tools {
    --edit-padding: 15px;
    --edit-padding-sides: 15px;
    max-width: 100vw;
    flex-grow: 0;
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    gap: 0px;
    width: 100%;
    /* padding: 10px; */
    /* height: 50px; */
    border-top: 1px solid black;
    /* padding-bottom: 20px; */
    /* padding-bottom: 0; */
    box-sizing: border-box;
    /* border-bottom: 1px solid black; */
  }
  .edit-tools .right,
  .edit-tools .left,
  .edit-tools .rest {
    padding: 0px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    height: 100%;
    flex-grow: 1;
  }
  .edit-tools .rest {
    padding: var(--edit-padding);
    padding-left: var(--edit-padding-sides);
    padding-right: var(--edit-padding-sides);
    justify-content: space-between;
    flex-grow: 1;
    box-sizing: border-box;
  }
  .edit-tools .right {
    justify-content: flex-end;
    padding-right: 0;
    align-items: center;
  }
  .edit-panel-container {
    flex-grow: 0;
    flex-shrink: 1;
    display: flex;
    /* overflow: hidden; */
    box-sizing: border-box;

    /* overflow: hidden; */
    /* -top: 10px; */
    width: 100%;
  }
  .innerScroll .edit-panel-container {
    height: 100%;
  }
  @media screen and (max-width: 720px) {
    .container {
      /* height: 100%; */
      /* width: 100%; */
      flex-direction: column;
    }
    .edit-panel {
      padding: 0px;
    }
    .edit-tools {
      justify-content: flex-end;
      /* padding: 10px; */
      /* padding-top: 10px; */
      /* padding-bottom: 20px; */
    }
    .artwork-panel.sidePanelVisible {
      border-right: 0;
      /* border-bottom: 1px dashed black; */
      /* border-bottom: 3px solid black; */
      /* border-bottom: 2px solid black; */
      border-bottom: 1px solid black;
    }
  }
</style>
